import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Linkedin, X, Youtube, Heart, ArrowUpRight } from 'lucide-react';
import { useTheme } from "../Home/ThemeContext";

// ⚠️ Logo Path Preserved
import logo from '../../img/image.png'; 

export default function Footer() {
  const { isDarkMode } = useTheme();

  // Modernized Professional Theme Config
  const theme = {
    wrapper: isDarkMode 
      ? 'bg-gradient-to-b from-slate-900 via-slate-950 to-black border-slate-800 text-slate-400' 
      : 'bg-gradient-to-b from-white via-slate-50 to-indigo-50/40 border-indigo-100 text-slate-600',
    
    textHead: isDarkMode ? 'text-white' : 'text-slate-900',
    textSub: isDarkMode ? 'text-slate-400' : 'text-slate-600',
    
    // Social Buttons
    socialBtn: isDarkMode
      ? 'bg-slate-800/80 text-slate-300 hover:bg-indigo-600 hover:text-white border border-slate-700/60 shadow-sm'
      : 'bg-white text-slate-600 hover:bg-indigo-600 hover:text-white border border-slate-200 shadow-sm',
      
    divider: isDarkMode ? 'border-slate-800/80' : 'border-indigo-100/80',
    hoverLink: isDarkMode ? 'hover:text-indigo-400' : 'hover:text-indigo-600',
  };

  return (
    <footer className={`relative w-full border-t pt-12 pb-6 overflow-hidden transition-colors duration-500 ${theme.wrapper}`}>
      
      {/* Subtle Background Glow */}
      <div className={`absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[150px] opacity-15 blur-[100px] rounded-full pointer-events-none ${isDarkMode ? 'bg-indigo-600' : 'bg-indigo-300'}`} />

      {/* Main Container - Optimized max-w-6xl */}
      <div className="relative max-w-6xl mx-auto px-5 lg:px-8">

        {/* TOP GRID SECTION */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 lg:gap-12 pb-10 border-b border-inherit">
          
          {/* Brand & Mission Column (Span 4) */}
          <div className="md:col-span-4 space-y-4">
            <Link to="/" className="inline-flex items-center gap-2.5 group">
              <img 
                src={logo} 
                alt="Vidyazon Logo" 
                className="h-8 w-auto object-contain transition-transform duration-500 group-hover:scale-105" 
              />
              <span className={`text-xl font-black tracking-wide ${theme.textHead}`}>
                VIDYA<span className="text-indigo-600">ZON</span>
              </span>
            </Link>
            <p className={`text-xs leading-relaxed max-w-sm ${theme.textSub}`}>
              AI precision meets expert mentorship. Your trusted compass for global higher education and dream careers.
            </p>
            {/* Social Icons inside Brand Column for mobile/desktop layout balance */}
            <div className="flex items-center gap-2.5 pt-2">
              {[
                { Icon: Youtube, href: "https://www.youtube.com/@vidyazon", label: "YouTube" },
                { Icon: Instagram, href: "https://www.instagram.com/vidyazon_/", label: "Instagram" },
                { Icon: Linkedin, href: "https://www.linkedin.com/in/vidyazon-c-o-aespleduzone-a8857539b", label: "LinkedIn" },
                { Icon: Facebook, href: "https://www.facebook.com/vidyazon", label: "Facebook" },
                { Icon: X, href: "https://x.com/AesplEduzone", label: "X" }
              ].map(({ Icon, href, label }, index) => (
                <a 
                  key={index} 
                  href={href} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  aria-label={label}
                  className={`p-2 rounded-xl transition-all duration-300 hover:-translate-y-1 ${theme.socialBtn}`}
                >
                  <Icon size={14} />
                </a>
              ))}
            </div>
          </div>

          {/* Navigation Links Columns (Span 8 total) */}
          <div className="md:col-span-8 grid grid-cols-2 sm:grid-cols-3 gap-6 lg:gap-8">
            
            {/* Company Links */}
            <div className="space-y-3">
              <h5 className={`font-bold text-xs uppercase tracking-wider ${theme.textHead}`}>Company</h5>
              <ul className="space-y-2.5">
                {[
                  { name: 'Home', path: '/' },
                  { name: 'Blog', path: '/blog' },
                  { name: 'About Us', path: '/about' },
                  { name: 'Contact Us', path: '/contact' },
                  { name: 'FAQs', path: '/faq' },
                ].map((link) => (
                  <li key={link.name}>
                    <Link 
                      to={link.path} 
                      className={`text-xs font-medium flex items-center gap-1 transition-colors ${theme.textSub} ${theme.hoverLink}`}
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Legal Links */}
            <div className="space-y-3">
              <h5 className={`font-bold text-xs uppercase tracking-wider ${theme.textHead}`}>Legal & Policies</h5>
              <ul className="space-y-2.5">
                {[
                  { name: 'Privacy Policy', path: '/privacy-policy' },
                  { name: 'Refund Policy', path: '/refund-policy' },
                  { name: 'Terms of Service', path: '/t&c' }
                ].map((link) => (
                  <li key={link.name}>
                    <Link 
                      to={link.path} 
                      className={`text-xs font-medium flex items-center gap-1 transition-colors ${theme.textSub} ${theme.hoverLink}`}
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Quick Support / Portal info */}
            <div className="space-y-3 col-span-2 sm:col-span-1">
              <h5 className={`font-bold text-xs uppercase tracking-wider ${theme.textHead}`}>Explore More</h5>
              <ul className="space-y-2.5">
                <li>
                  <Link to="/student/colleges" className={`text-xs font-medium flex items-center gap-1 transition-colors ${theme.textSub} ${theme.hoverLink}`}>
                    College Selection <ArrowUpRight size={12} className="opacity-70" />
                  </Link>
                </li>
                <li>
                  <Link to="/student/programs" className={`text-xs font-medium flex items-center gap-1 transition-colors ${theme.textSub} ${theme.hoverLink}`}>
                    Master's Programs <ArrowUpRight size={12} className="opacity-70" />
                  </Link>
                </li>
                <li>
                  <Link to="/student/scholarships" className={`text-xs font-medium flex items-center gap-1 transition-colors ${theme.textSub} ${theme.hoverLink}`}>
                    Scholarships <ArrowUpRight size={12} className="opacity-70" />
                  </Link>
                </li>
              </ul>
            </div>

          </div>
        </div>

        {/* BOTTOM COPYRIGHT & ATTRIBUTION BAR */}
        <div className={`pt-6 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs ${theme.textSub}`}>
          <p className="text-[11px] font-medium tracking-wide">
            &copy; {new Date().getFullYear()} Vidyazon. All rights reserved.
          </p>

          <p className="text-[11px] font-medium flex items-center gap-1.5">
            Designed with <Heart size={11} className="text-rose-500 fill-rose-500 animate-pulse" /> by 
            <a href="#" className={`font-bold transition-colors hover:underline ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>
              Shivansh
            </a>
          </p>
        </div>

      </div>
    </footer>
  );
}